import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import GlobalLayout from './GlobalLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { appConfig } from '../config'; 
import './QuizBuilder.css'; // Umbenannte CSS Datei
import { fetchWithAuth } from '../services/apiService';


// --- SECURITY HELPER ---
const sanitizeInput = (str) => {
    if (!str) return '';
    // Entfernt HTML Tags
    let clean = str.replace(/<[^>]*>?/gm, '');
    return clean.trim();
};

const QuizBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation(); // <--- Hook initialisieren
  const { t, currentLanguage } = useLanguage();
  const { user, token, makeAuthenticatedRequest, features, hasFeature } = useAuth();

  // ── Zugriffskontrolle: features.quiz_builder ─────────────────────────────
  useEffect(() => {
    if (!hasFeature('quiz_builder')) {
      navigate('/', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ─────────────────────────────────────────────────────────────────────────

  // --- STEPS ---
  // 1: Basics (Name & Typ)
  // 2: Details (Desc & Image)
  // 3: Config (Frage/Antworten/QR)
  // 4: Responses (Videos)
  // 5: Review
  // 6: Success
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);

  // --- DATA STATE ---
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quiz_type: 1, // 1=Yes/No, 2=QR, 3=Multi (ehemals challenge_type)
    hint_active: false,
    thumbnailFile: null,
    thumbnailPreview: null,
    correct_option: '1', 
    
    // Multi Option
    number_of_options: 2,
    use_option_names: true, 
    options: [
        { name: '', file: null, preview: null }, 
        { name: '', file: null, preview: null }, 
        { name: '', file: null, preview: null }, 
        { name: '', file: null, preview: null }, 
        { name: '', file: null, preview: null }, 
        { name: '', file: null, preview: null }
    ],

    // Responses
    response_correct_hash: '',
    response_incorrect_hash: '',
    
    // UI Helpers for preview
    response_correct_thumb: '',
    response_incorrect_thumb: ''
  });

  // --- RESPONSE SELECTOR MODAL ---
  const [showElementSelector, setShowElementSelector] = useState(false);
  const [selectorTarget, setSelectorTarget] = useState(null); 
  const [availableResponseVideos, setAvailableResponseVideos] = useState([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);

  // --- HELPERS ---
  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const handleOptionFile = (index, file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], file: file, preview: preview };
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const fetchResponseElements = async () => {
    setIsLoadingVideos(true);
    try {
        const data = await makeAuthenticatedRequest('/elements/all');
        // FILTER: Nur content_type = 4
        const filtered = data.filter(el => parseInt(el.content_type) === 4);
        setAvailableResponseVideos(filtered);
    } catch (e) {
        console.error(e);
    } finally {
        setIsLoadingVideos(false);
    }
  };

  const handleMainThumbnail = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        thumbnailFile: file,
        thumbnailPreview: URL.createObjectURL(file)
      }));
    }
  };

  const selectResponseVideo = (element) => {
      if (selectorTarget === 'correct') {
          setFormData(prev => ({ 
              ...prev, 
              response_correct_hash: element.hash_id, 
              response_correct_thumb: element.url_thumbnail 
          }));
      } else {
          setFormData(prev => ({ 
              ...prev, 
              response_incorrect_hash: element.hash_id, 
              response_incorrect_thumb: element.url_thumbnail 
          }));
      }
      setShowElementSelector(false);
  };

  // --- NEU: SCHLIESSEN-HANDLER ---
  const handleClose = () => {
      if (location.state && location.state.returnUrl) {
          // Zurück zum CourseBuilder (oder woher wir kamen)
          navigate(location.state.returnUrl);
      } else {
          // Standard: Ab zum Dashboard
          navigate('/dashboard');
      }
  };

 const handleSubmit = async () => {

      // 1. Validierung & Sanitization Start
      const cleanName = sanitizeInput(formData.name);
      if (!cleanName) return setError(t('pleaseEnterName') || 'Bitte gib einen gültigen Namen ein.');
      
      const cleanDesc = sanitizeInput(formData.description);

      let authToken = token;
      if (!authToken) {
        authToken = localStorage.getItem('token') || localStorage.getItem('hejvi_access_token');
      }
      if (!authToken) throw new Error(t('authErrorTokenMissing'));

      setIsProcessing(true);
      
      const data = new FormData();
      data.append('name', cleanName);
      data.append('description', cleanDesc);
      
      if (formData.thumbnailFile) {
          data.append('thumbnail', formData.thumbnailFile);
      }
      data.append('quiz_type', formData.quiz_type);
      data.append('hint_active', formData.hint_active ? '1' : '0');
      data.append('response_correct_hash', formData.response_correct_hash);
      data.append('response_incorrect_hash', formData.response_incorrect_hash);

      // TYP-SPEZIFISCHE SANITIZATION
      if (formData.quiz_type === 1) { 
          // Yes/No (Ist immer '0' oder '1', sicher)
          data.append('correct_option', formData.correct_option); 
      } 
      else if (formData.quiz_type === 2) { 
          // QR Code: Hier kann Text stehen -> Sanitizen!
          const cleanQR = sanitizeInput(formData.correct_option);
          if (!cleanQR) {
              setIsProcessing(false);
              return setError(t('qrErrorEmpty') || 'Ungültiger QR Wert.');
          }
          data.append('correct_option', cleanQR); 
      } 
      else if (formData.quiz_type === 3) { 
          // Multi Option
          data.append('number_of_options', formData.number_of_options);
          data.append('use_option_names', formData.use_option_names ? '1' : '0');
          data.append('correct_option', formData.correct_option); 

          for (let i = 0; i < formData.number_of_options; i++) {
              const opt = formData.options[i];
              
              // Option Namen säubern
              const cleanOptName = sanitizeInput(opt.name);
              
              // Wenn Text-Modus aktiv ist und Name leer (nach sanitize), ist das ein Fehler
              if (formData.use_option_names && !cleanOptName) {
                  setIsProcessing(false);
                  return setError(`${t('errorOptionMissing') || 'Ungültiger Text bei Option'} ${i+1}`);
              }

              data.append(`option${i+1}_name`, cleanOptName);
              
              if (opt.file) {
                  data.append(`option${i+1}_thumb`, opt.file);
              }
          }
      }
      
      data.append('source_language', currentLanguage || 'de');

      try {
        const baseUrl = appConfig.apiBaseUrl.replace(/\/$/, '');
        
            // 3. Fetch mit Token-Refresh-Unterstützung OHNE Content-Type Header (WICHTIG für FormData!)
        const res = await fetchWithAuth(`${baseUrl}/create-quiz`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${authToken}`
            },
            body: data
        });

        // 401 gesondert behandeln
        if (res.status === 401) {
            throw new Error("Nicht autorisiert (401). Dein Login ist abgelaufen.");
        }

        // Antwort parsen
        const result = await res.json();

        if (!res.ok || result.status === 'error') {
            let errorMsg = result.message || "Ein Fehler ist aufgetreten (Server Error).";
            
            // Check auf Moderations-Fehler
            if (errorMsg.includes('Content rejected') || errorMsg.includes('moderation')) {
                errorMsg = t('errorContentRejected') || 'Dein Inhalt wurde abgelehnt, da er gegen die Sicherheitsrichtlinien verstößt.';
            }

            throw new Error(errorMsg);
        }
        
        // Erfolgsfall
        if (result.status === 'success') {
            // --- Workflow Logic ---
            const draft = sessionStorage.getItem('hejvi_course_draft');
            if (draft) {
                const newQuizForBuilder = {
                    id: result.id || result.quizId, 
                    name: formData.name,
                    description: formData.description,
                    url_thumbnail: result.url_thumbnail || '', 
                    type: 2, 
                    content_type: 2
                };

                sessionStorage.setItem('hejvi_created_element', JSON.stringify(newQuizForBuilder));
                navigate('/create-course');
                return;
            }
            // ---------------------------

            setSuccessData(result);
            setStep(6);
        }
      } catch (e) {
          console.error("Submit Error:", e);
          setError(e.message || "Ein unbekannter Fehler ist aufgetreten.");
      } finally {
          setIsProcessing(false);
      }
  };

  // --- RENDERERS ---

  // STEP 1: BASICS
  // STEP 1: BASICS
  const renderStep1_Basics = () => (
    <div className="qb-step fade-in">
        <h2>{t('createQuiz') || 'Create Quiz'}</h2>
        
        <div className="qb-form-group">
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                <label>{t('question') || 'Question / Headline'} <span style={{color:'red'}}>*</span></label>
                <span style={{fontSize: '12px', color: formData.name.length >= 120 ? 'red' : '#999'}}>
                    {formData.name.length}/120
                </span>
            </div>
            <input 
                type="text" 
                value={formData.name} 
                onChange={e => setFormData({...formData, name: e.target.value})} 
                placeholder={t('QuizQuestionPlaceholder') || 'e.g. Is this safety gear correct?'}
                autoFocus
                maxLength={120} // LIMIT 120
                style={{fontSize: '18px', padding: '14px'}}
            />
        </div>

        <label className="qb-label" style={{marginTop: '30px'}}>{t('selectType') || 'Select Type'}</label>
        <div className="qb-type-grid">
            {[
                {id: 1, label: t('typeYesNo') || 'Yes / No', icon: '👍/👎'},
                {id: 2, label: t('typeQR') || 'QR Scan', icon: '📷'},
                {id: 3, label: t('typeMulti') || 'Multi Option', icon: 'abcd'}
            ].map(type => (
                <div 
                    key={type.id}
                    className={`qb-type-card ${formData.quiz_type === type.id ? 'active' : ''}`}
                    onClick={() => setFormData({
                        ...formData, 
                        quiz_type: type.id, 
                        correct_option: type.id === 1 ? '1' : '',
                        hint_active: type.id === 2 ? false : formData.hint_active
                    })}
                >
                    <div className="qb-type-icon">{type.icon}</div>
                    <div>{type.label}</div>
                </div>
            ))}
        </div>
    </div>
  );

  // STEP 2: DETAILS (OPTIONAL)
  const renderStep2_Details = () => (
    <div className="qb-step fade-in">
        <h2>{t('details') || 'Details'}</h2>
        <div className="qb-form-group">
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                <label>{t('description')} <span style={{fontWeight:'normal', color:'#999'}}>(Optional)</span></label>
                <span style={{fontSize: '12px', color: formData.description.length >= 160 ? 'red' : '#999'}}>
                    {formData.description.length}/160
                </span>
            </div>
            <textarea 
                value={formData.description} 
                onChange={e => setFormData({...formData, description: e.target.value})} 
                placeholder={t('QuizDescriptionPlaceholder') || 'Optional description'}
                rows={4}
                maxLength={160} // LIMIT 160
            />
        </div>
        
        {/* --- MEDIUM COMPACT THUMBNAIL UPLOAD --- */}
        <div className="qb-form-group" style={{marginTop: '20px'}}>
            <label style={{fontSize: '14px', marginBottom: '8px', display:'block', fontWeight: 600}}>
                {t('thumbnail') || 'Cover Bild'} <span style={{fontWeight:'normal', color:'#999'}}>(Optional)</span>
            </label>
            
            <div 
                className="qb-upload-area" 
                style={{
                    border: '2px dashed #e5e7eb', 
                    borderRadius: '12px', 
                    padding: '12px 16px', 
                    cursor: 'pointer',
                    background: '#f9fafb',
                    display: 'flex',
                    flexDirection: 'row', // Horizontales Layout spart Platz
                    alignItems: 'center',
                    gap: '16px',
                    maxHeight: '240px', // Viel flacher als vorher
                    width: '100%'
                }}
                onClick={() => document.getElementById('quiz-thumb-upload').click()}
            >
                {formData.thumbnailPreview ? (
                    // 1. ZUSTAND: BILD VORHANDEN
                    <>
                        <div style={{position: 'relative', width: '64px', height: '64px', flexShrink: 0}}>
                            <img 
                                src={formData.thumbnailPreview} 
                                alt="Preview" 
                                style={{
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover', 
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb'
                                }} 
                            />
                        </div>
                        <div style={{flex: 1}}>
                            <span style={{fontSize: '14px', color: '#10B981', fontWeight: 600, display: 'block'}}>
                                ✓ {t('imageSelected') || 'Geladen'}
                            </span>
                            <span style={{fontSize: '12px', color: '#6B7280'}}>
                                Tippen zum Ändern
                            </span>
                        </div>
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setFormData(prev => ({...prev, thumbnailFile: null, thumbnailPreview: null}));
                            }}
                            style={{
                                background: '#EF4444', color: 'white', border: 'none', 
                                borderRadius: '50%', width: '28px', height: '28px', 
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                        >
                        ✕
                        </button>
                    </>
                ) : (
                    // 2. ZUSTAND: LEER
                    <>
                        <div style={{
                            width: '48px', height: '48px', 
                            background: '#e5e7eb', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '24px'
                        }}>
                            📷
                        </div>
                        <div style={{flex: 1}}>
                            
                            <span style={{fontSize: '12px', color: '#6B7280'}}>
                                {t('tapToUpload') || 'Hier tippen zum Auswählen...'}
                            </span>
                        </div>
                    </>
                )}
                
                <input 
                    id="quiz-thumb-upload" 
                    type="file" 
                    accept="image/*" 
                    hidden 
                    onChange={handleMainThumbnail}
                />
            </div>
        </div>
    </div>
  );
  
  // STEP 3: CONFIGURATION
  const renderStep3_Config = () => (
    <div className="qb-step fade-in">
        <h2>{t('configureQuiz') || 'Configure'}</h2>
        
        {/* HINT TOGGLE */}
        <div 
            className="qb-toggle-row" 
            style={formData.quiz_type === 2 ? {opacity: 0.5, pointerEvents: 'none'} : {}}
        >
            <span>{t('enableHint') || 'Enable Hint Button?'}</span>
            <label className="qb-switch">
                <input 
                    type="checkbox" 
                    checked={formData.hint_active}
                    disabled={formData.quiz_type === 2}
                    onChange={e => setFormData({...formData, hint_active: e.target.checked})}
                />
                <span className="qb-slider round"></span>
            </label>
        </div>
        
        {formData.quiz_type === 2 && (
             <p style={{fontSize:'12px', color:'#999', marginTop:'-10px', marginBottom:'20px'}}>
                 {t('hintNotAvailableQR') || 'Hints are disabled for QR Code challenges.'}
             </p>
        )}

        {/* TYPE 1: YES/NO */}
        {formData.quiz_type === 1 && (
            <div className="qb-config-box">
                <div className="qb-question-reminder" style={{textAlign: 'center', marginBottom: '8px'}}>
                    <small><span style={{fontStyle: 'italic'}}>{formData.name}</span></small>
                </div>
                <div style={{textAlign: 'center', marginBottom: '20px'}}>
                    <h3>{t('selectCorrectAnswer') || 'Richtige Antwort wählen'}</h3>
                    
                </div>

                <div className="qb-yn-grid">
                    <div 
                        className={`qb-yn-card yes ${formData.correct_option === '1' ? 'active' : ''}`}
                        onClick={() => setFormData({...formData, correct_option: '1'})}
                    >
                        <div className="qb-yn-content">
                            <span className="qb-yn-icon">👍</span>
                            <span className="qb-yn-label">{t('yes')}</span>
                        </div>
                        <div className="qb-yn-check">
                            {formData.correct_option === '1' && <div className="qb-yn-check-inner" />}
                        </div>
                    </div>

                    <div 
                        className={`qb-yn-card no ${formData.correct_option === '0' ? 'active' : ''}`}
                        onClick={() => setFormData({...formData, correct_option: '0'})}
                    >
                        <div className="qb-yn-content">
                            <span className="qb-yn-icon">👎</span>
                            <span className="qb-yn-label">{t('no')}</span>
                        </div>
                        <div className="qb-yn-check">
                            {formData.correct_option === '0' && <div className="qb-yn-check-inner" />}
                        </div>
                    </div>
                </div>
                
                <div className="qb-yn-feedback">
                    Wenn der Nutzer <strong> "{formData.correct_option === '1' ? t('yes') : t('no')}" </strong> wählt, gilt das Quiz als bestanden.
                </div>
            </div>
        )}

        {/* TYPE 2: QR SCAN */}
        {formData.quiz_type === 2 && (
            <div className="qb-config-box qb-qr-wrapper">
                <div className="qb-qr-visual">
                    <div className="qb-qr-icon">📱</div>
                    <div className="qb-qr-scan-line"></div>
                </div>
                <div className="qb-qr-content">
                    <h3>{t('qrTargetTitle') || "QR Code Ziel"}</h3>
                    <p className="qb-help-text">
                        {t('qrTargetDesc') || "Welcher Text/Code ist im QR-Code hinterlegt, den der Nutzer scannen muss?"}
                    </p>
                    <div className="qb-input-group">
                        <div style={{display:'flex', justifyContent:'space-between'}}>
                            <label>{t('qrValueLabel') || "QR Inhalt (Exakt)"}</label>
                            <span style={{fontSize: '11px', color: formData.correct_option.length >= 128 ? 'red' : '#999'}}>
                                {formData.correct_option.length}/128
                            </span>
                        </div>
                        <input 
                            type="text" 
                            className="qb-input-highlight" 
                            value={formData.correct_option}
                            onChange={e => setFormData({...formData, correct_option: e.target.value})}
                            placeholder={t('QuizExampleQrHash' || 'e.g. el-32hhuz3')}
                            maxLength={128} // LIMIT 128
                        />
                    </div>
                </div>
            </div>
        )}

        {/* TYPE 3: MULTI OPTION */}
        {formData.quiz_type === 3 && (
            <div className="qb-config-box">
                <div className="qb-row-spaced">
                    <label>Anzahl Optionen:</label>
                    <div className="qb-segment-control">
                        {[2, 4, 6].map(num => (
                            <button 
                                key={num}
                                className={formData.number_of_options === num ? 'active' : ''}
                                onClick={() => setFormData({...formData, number_of_options: num})}
                            >
                                {num}
                            </button>
                        ))}
                    </div>
                </div>

                <hr className="qb-divider" />

                <div className="qb-row-center" style={{marginBottom: '20px'}}>
                    <div className="qb-mode-pill">
                        <button 
                            className={formData.use_option_names ? 'active' : ''}
                            onClick={() => setFormData({...formData, use_option_names: true})}
                        >
                            📝 Text Liste
                        </button>
                        <button 
                            className={!formData.use_option_names ? 'active' : ''}
                            onClick={() => setFormData({...formData, use_option_names: false})}
                        >
                            🖼️ Bild Grid
                        </button>
                    </div>
                </div>

                <div className={`qb-options-container ${formData.use_option_names ? 'mode-list' : 'mode-grid'}`}>
                    {Array.from({length: formData.number_of_options}).map((_, idx) => (
                        <div 
                            key={idx} 
                            className={`qb-option-card ${String(formData.correct_option) === String(idx + 1) ? 'is-correct' : ''}`}
                            onClick={() => !formData.use_option_names && setFormData({...formData, correct_option: String(idx + 1)})}
                        >
                            <div className="qb-opt-top">
                                <span className="qb-opt-num">{idx + 1}</span>
                                <div 
                                    className="qb-opt-radio" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFormData({...formData, correct_option: String(idx + 1)});
                                    }}
                                >
                                    <div className={`radio-circle ${String(formData.correct_option) === String(idx + 1) ? 'checked' : ''}`} />
                                    {formData.use_option_names && <span>Ist korrekt?</span>}
                                </div>
                            </div>

                            {formData.use_option_names && (
                                <div style={{width: '100%'}}>
                                    <input 
                                        type="text"
                                        className="qb-input-clean"
                                        placeholder={`Antwort Option ${idx+1}...`}
                                        value={formData.options[idx].name}
                                        onChange={e => updateOption(idx, 'name', e.target.value)}
                                        autoFocus={idx === 0}
                                        maxLength={120} // LIMIT 120
                                    />
                                    <div style={{textAlign: 'right', fontSize: '10px', color: '#ccc', marginTop: '2px'}}>
                                        {formData.options[idx].name.length}/120
                                    </div>
                                </div>
                            )}

                            {!formData.use_option_names && (
                                <div className="qb-upload-area">
                                    {formData.options[idx].preview ? (
                                        <div className="qb-img-preview-wrapper">
                                            <img src={formData.options[idx].preview} alt="" />
                                            <button 
                                                className="qb-remove-img"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOptionFile(idx, null);
                                                }}
                                            >✕</button>
                                        </div>
                                    ) : (
                                        <label className="qb-upload-label">
                                            <span>📷</span>
                                            <small>Bild wählen</small>
                                            <input 
                                                type="file" 
                                                accept="image/*"
                                                onChange={e => handleOptionFile(idx, e.target.files[0])}
                                            />
                                        </label>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
  );

  // STEP 4: RESPONSES
  const renderStep4_Responses = () => (
    <div className="qb-step fade-in">
        <h2>{t('selectResponses') || 'Select Responses'}</h2>
        <p className="qb-subtitle">
            {t('selectResponsesDesc') || 'Wähle die Videos aus, die abgespielt werden, je nachdem ob der Nutzer richtig oder falsch antwortet.'}
        </p>

        <div className="qb-response-grid">
            <div className="qb-response-card">
                <div className="qb-resp-header is-correct">
                    <span>✅ {t('correct') || "Richtig"}</span>
                    {formData.response_correct_hash && <span>✓</span>}
                </div>
                <div 
                    className="qb-resp-body"
                    onClick={() => { setSelectorTarget('correct'); fetchResponseElements(); setShowElementSelector(true); }}
                >
                    {formData.response_correct_thumb ? (
                        <>
                            <div className="qb-selected-preview">
                                <img src={formData.response_correct_thumb} alt="Selected" />
                                <div className="qb-change-overlay">
                                    <span className="qb-change-btn">Wechseln</span>
                                </div>
                            </div>
                            <div className="qb-video-title">{formData.response_correct_name || 'Video ausgewählt'}</div>
                        </>
                    ) : (
                        <div className="qb-placeholder-state">
                            <div className="qb-icon-circle">+</div>
                            <span>Video wählen</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="qb-response-card">
                <div className="qb-resp-header is-incorrect">
                    <span>❌ {t('incorrect') || "Falsch"}</span>
                    {formData.response_incorrect_hash && <span>✓</span>}
                </div>
                <div 
                    className="qb-resp-body"
                    onClick={() => { setSelectorTarget('incorrect'); fetchResponseElements(); setShowElementSelector(true); }}
                >
                    {formData.response_incorrect_thumb ? (
                        <>
                            <div className="qb-selected-preview">
                                <img src={formData.response_incorrect_thumb} alt="Selected" />
                                <div className="qb-change-overlay">
                                    <span className="qb-change-btn">Wechseln</span>
                                </div>
                            </div>
                            <div className="qb-video-title">{formData.response_incorrect_name || 'Video ausgewählt'}</div>
                        </>
                    ) : (
                        <div className="qb-placeholder-state">
                            <div className="qb-icon-circle">+</div>
                            <span>Video wählen</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
  );

  // STEP 5: REVIEW
  const renderStep5_Review = () => {
    const typeLabels = { 1: 'Yes / No', 2: 'QR Scan', 3: 'Multi Option' };
    
    return (
        <div className="qb-step fade-in">
            <h2>{t('reviewQuiz') || 'Zusammenfassung'}</h2>
            <p className="qb-subtitle">{t('reviewDesc') || 'Bitte prüfe deine Angaben, bevor du das Quiz erstellst.'}</p>

            <div className="qb-review-container">
                <div className="qb-review-section">
                    <h3>📄 Allgemein</h3>
                    <div className="qb-review-row">
                        <span>Name:</span> <strong>{formData.name}</strong>
                    </div>
                    <div className="qb-review-row">
                        <span>Beschreibung:</span> <span className="qb-text-muted">{formData.description || '-'}</span>
                    </div>
                    <div className="qb-review-row">
                        <span>Typ:</span> <strong>{typeLabels[formData.quiz_type]}</strong>
                    </div>
                    <div className="qb-review-row">
                        <span>Hinweis-Button:</span> 
                        <strong>{formData.hint_active ? '✅ Aktiv' : '🚫 Inaktiv'}</strong>
                    </div>
                </div>

                <div className="qb-review-section">
                    <h3>🧩 Konfiguration & Lösung</h3>
                    {formData.quiz_type === 1 && (
                        <div className="qb-review-row">
                            <span>Korrekte Antwort:</span> 
                            <strong style={{color: formData.correct_option === '1' ? '#4CAF50' : '#F44336'}}>
                                {formData.correct_option === '1' ? 'JA' : 'NEIN'}
                            </strong>
                        </div>
                    )}
                    {formData.quiz_type === 2 && (
                        <div className="qb-review-box-highlight">
                            <span>QR Code Zielwert:</span>
                            <code>{formData.correct_option}</code>
                        </div>
                    )}
                    {formData.quiz_type === 3 && (
                        <div className="qb-review-list">
                            <span>Optionen ({formData.number_of_options}):</span>
                            <ul>
                                {Array.from({length: formData.number_of_options}).map((_, idx) => (
                                    <li key={idx} className={String(formData.correct_option) === String(idx + 1) ? 'correct' : ''}>
                                        {formData.options[idx].preview && <img src={formData.options[idx].preview} alt="Thumb" />}
                                        <span>
                                            {formData.options[idx].name || `Option ${idx+1}`}
                                        </span>
                                        {String(formData.correct_option) === String(idx + 1) && <span className="check">✅</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="qb-review-section">
                    <h3>🎬 Reaktion</h3>
                    <div className="qb-review-videos">
                        <div className="qb-rv-item">
                            <span className="label-correct">Richtig</span>
                            <img src={formData.response_correct_thumb} alt="Correct" />
                            <span className="vid-name">{formData.response_correct_name}</span>
                        </div>
                        <div className="qb-rv-item">
                            <span className="label-incorrect">Falsch</span>
                            <img src={formData.response_incorrect_thumb} alt="Incorrect" />
                            <span className="vid-name">{formData.response_incorrect_name}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
  };

  // STEP 6: SUCCESS
  const renderStep6_Success = () => (
    <div className="qb-step qb-success fade-in">
        <div className="qb-icon-lg">🎉</div>
        <h2>{t('quizCreatedSuccess' || "Quiz successfully created!")}</h2>
        
        {successData && successData.qr_direct_url && (
            <div className="qb-qr-box">
                <img src={successData.qr_direct_url} alt="QR" />
                <span>{t('scanToStart')}</span>
            </div>
        )}

        <div className="qb-actions" style={{display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center'}}>
            {successData && successData.hash_id && (
                <button 
                    className="qb-btn-pri" 
                    style={{backgroundColor: '#10B981', borderColor: '#10B981'}} 
                    onClick={() => navigate(`/element/${successData.hash_id}`)}
                >
                     {t('viewQuiz') || "Quiz ansehen"}
                </button>
            )}
            <button className="qb-btn-sec" onClick={() => navigate('/dashboard')}>
                {t('backToDashboard') || "Zum Dashboard"}
            </button>
            <button className="qb-btn-pri" onClick={() => window.location.reload()}>{t('createAnother')}</button>
        </div>
    </div>
  );

  // --- NAVIGATION HANDLER ---
  const handleNext = () => {
    setError(''); 

    // VALIDIERUNG STEP 1 (Name)
    if (step === 1) {
        if (!formData.name || formData.name.trim() === '') {
            setError(t('pleaseEnterQuestion') || 'Bitte gib eine Frage oder Titel ein.');
            return;
        }
    }

    // VALIDIERUNG STEP 2 (Details) - Alles optional
    if (step === 2) {
        // Nichts zu tun
    }

    // VALIDIERUNG STEP 3 (Config)
    if (step === 3) {
        if (formData.quiz_type === 1) {
            if (!formData.correct_option) {
                setError(t('errorSelectCorrect') || 'Bitte wähle die korrekte Antwort aus.');
                return;
            }
        }
        if (formData.quiz_type === 2) {
             if (!formData.correct_option || formData.correct_option.trim() === '') {
                 setError(t('qrErrorEmpty') || 'Bitte QR Code Wert eingeben.');
                 return;
             }
        }
        if (formData.quiz_type === 3) {
            if (!formData.correct_option) {
                setError(t('errorSelectCorrect') || 'Bitte markiere, welche Option die richtige ist.');
                return;
            }
            for (let i = 0; i < formData.number_of_options; i++) {
                const opt = formData.options[i];
                const hasName = opt.name && opt.name.trim() !== '';
                const hasImage = opt.file || opt.preview; 
                if (!hasName && !hasImage) {
                    setError(`${t('errorOptionMissing') || 'Bitte fülle Option'} ${i+1} (Text oder Bild).`);
                    return;
                }
            }
        }
    }

    // VALIDIERUNG STEP 4 (Responses)
    if (step === 4) {
        if (!formData.response_correct_hash || !formData.response_incorrect_hash) {
            setError(t('selectBothResponses') || 'Bitte wähle für Richtig und Falsch je ein Video aus.');
            return;
        }
    }

    setStep(prev => prev + 1);
  };

  return (
    <GlobalLayout>
        {/* WICHTIG: Flex-Layout wie im ElementCreator, damit Footer automatisch unten sitzt */}
        <div className="qb-container" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
            
            {/* Progress: Jetzt 5 Schritte bis zum Review */}
            <div className="qb-progress">
                {[1,2,3,4,5].map(s => <div key={s} className={`qb-bar ${step >= s ? 'active' : ''}`} />)}
            </div>

            {error && <div className="qb-error">{error}</div>}

            {/* Content: Nimmt den verfügbaren Platz ein (flex: 1) */}
            <div className="qb-content" style={{ flex: 1, paddingBottom: '20px' }}>
                {step === 1 && renderStep1_Basics()}
                {step === 2 && renderStep2_Details()}
                {step === 3 && renderStep3_Config()}
                {step === 4 && renderStep4_Responses()}
                {step === 5 && renderStep5_Review()} 
                {step === 6 && renderStep6_Success()} 
            </div>

            {/* Footer: Positionierung im Fluss (nicht fixed), Abstand nach oben */}
            {step < 6 && (
                <div className="qb-footer" style={{ 
                    padding: '20px', 
                    marginTop: '20px', 
                    borderTop: '1px solid #e5e7eb',
                    background: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    
                }}>
                    {/* LINKER BUTTON: IMMER SICHTBAR (Dashboard oder Step Back) */}
                    <button 
                        className="qb-btn-sec" 
                        onClick={() => {
                            // Wenn wir nicht im ersten Schritt sind -> Einen Schritt zurück
                            if (step > 1) {
                                setStep(s => s - 1);
                                return;
                            }

                            // Wenn wir im ersten Schritt sind (Abbrechen/Verlassen):
                            // 1. Prüfen, ob wir woanders herkommen (z.B. CourseBuilder)
                            if (location.state && location.state.returnUrl) {
                                navigate(location.state.returnUrl);
                            } else {
                                // 2. Fallback (Standardmäßig zum Dashboard)
                                navigate('/profile'); 
                            }
                        }}
                    >
                        {t('back') || 'Zurück'}
                    </button>
                    
                    {/* RECHTER BUTTON: WEITER oder ERSTELLEN */}
                    {step < 5 ? (
                        <button className="qb-btn-pri" onClick={handleNext}>{t('next') || 'Weiter'}</button>
                    ) : (
                        <button 
                            className="qb-btn-pri" 
                            onClick={handleSubmit} 
                            disabled={isProcessing}
                            style={{ minWidth: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="qb-btn-spinner"></span>
                                    {t('saving') || 'Speichern...'}
                                </>
                            ) : (
                                t('create') || 'Erstellen'
                            )}
                        </button>
                    )}
                </div>
            )}

            {/* Modal */}
            {showElementSelector && (
                <div className="qb-modal-overlay">
                    <div className="qb-modal">
                        <h3>Select {selectorTarget} video</h3>
                        <button className="qb-close" onClick={() => setShowElementSelector(false)}>✕</button>
                        
                        {isLoadingVideos ? <div className="qb-loading">Loading...</div> : (
                            <div className="qb-grid">
                                {availableResponseVideos.length === 0 && <p>No reaction videos found (content_type=4).</p>}
                                {availableResponseVideos.map(el => (
                                    <div key={el.id} className="qb-grid-item" onClick={() => selectResponseVideo(el)}>
                                        <img src={el.url_thumbnail} alt={el.name} />
                                        <span>{el.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    </GlobalLayout>
  );
};

export default QuizBuilder;